import dynamic from 'next/dynamic';

import type * as Pricing from '@/pricing/types';
import { Badge } from '@/ui/Badge/Badge';
import { makePrezlyLinkRelative } from '@/util/seo/makePrezlyLinkRelative';

import styles from './Cell.module.scss';

const Tooltip = dynamic(() => import('@/ui/Tooltip/Tooltip').then((mod) => mod.Tooltip), {
    ssr: false,
});

interface Props {
    row: Pricing.Row;
}

export function TitleCell({ row }: Props) {
    const hasTooltip = Boolean(row.description || row.status);

    if (hasTooltip) {
        return (
            <Tooltip
                placement="right"
                tooltip={
                    <div className={styles.tooltip}>
                        <h5>{row.display_name}</h5>
                        {row.description && <p>{row.description}</p>}
                        {row.status && (
                            <div className={styles.status}>
                                <Badge className={styles.statusBadge}>{row.status}</Badge>{' '}
                                <span>This feature is in testing</span>
                            </div>
                        )}
                        {row.link && (
                            <div className={styles.link}>
                                <a
                                    className={styles.learnMore}
                                    href={makePrezlyLinkRelative(row.link)}
                                >
                                    Learn more
                                </a>
                            </div>
                        )}
                    </div>
                }
            >
                {({ ariaAttributes, onHide, onShow, setReferenceElement }) => (
                    <div className={styles.columnName}>
                        {/* eslint-disable-next-line jsx-a11y/no-static-element-interactions */}
                        <span
                            {...ariaAttributes}
                            className={styles.hasTooltip}
                            onMouseEnter={onShow}
                            onMouseLeave={onHide}
                            ref={setReferenceElement}
                        >
                            {row.display_name}
                        </span>
                        {row.status && <Badge className={styles.badge}>{row.status}</Badge>}
                    </div>
                )}
            </Tooltip>
        );
    }

    return (
        <div className={styles.columnName}>
            {row.link ? (
                <a href={makePrezlyLinkRelative(row.link)}>{row.display_name}</a>
            ) : (
                <span>{row.display_name}</span>
            )}
        </div>
    );
}
